/**
 * commission.ts — la commission d'un chatteur, calculée à UN endroit.
 *
 * `commission = brut_usd × NET_REVENUE_MULTIPLIER × % / 100`, où le % est le
 * `accountMembers.salesCommissionPercent` **vivant** lu depuis Convex.
 *
 * ─── Pourquoi ce module existe (23-08-2026) ───────────────────────────────
 *
 * Le backend exposait un `commission_due_cents` calculé en SQL depuis
 * `Order.commission_percent_snapshot` — un % censé être figé sur chaque
 * commande AU MOMENT de l'envoi. Mesuré sur la prod le 23-08 : cette colonne
 * était NULL sur **5246 commandes sur 5246**, depuis sa création le 27-05.
 * Elle n'a jamais porté une seule valeur.
 *
 * La cause : un contrat à deux côtés où chacun avait documenté l'autre comme
 * l'implémenteur. Le backend attendait le % dans le corps de requête (« lu
 * depuis Convex par le cockpit ») ; le cockpit annonçait l'inverse (« lu par
 * le backend au moment de l'envoi »). La seule occurrence de
 * `operator_commission_percent` dans tout l'historique du cockpit est ce
 * commentaire — le champ n'a jamais été mis dans un payload.
 *
 * Conséquence : quatre surfaces affichaient « $0 » en permanence — la
 * pastille de titlebar (« ce qui motive au quotidien »), la perf par membre du
 * panneau Team, le KPI « Gagné » d'Analytics et le cumul du shift.
 *
 * ⚠️ Aucun chatteur n'a été sous-payé pour autant : tout ce qui PAIE
 * (`payroll/commission.py` côté backend, `WalletTab`/`payoutCycle`/
 * `MembersTable` côté cockpit) calcule déjà sur le % vivant. Le snapshot était
 * ORPHELIN — le modèle de payout v2 (08-07-2026) avait adopté le taux vivant
 * partout, et cette colonne de mai était restée derrière.
 *
 * Décision de Kayla (23-08-2026) : **on supprime le snapshot**, tout se
 * calcule sur le % vivant, et on ne rétro-remplit RIEN — le % vivant couvre
 * déjà l'historique, et aucun versement n'en dépendait.
 *
 * ⛔ Ne PAS réintroduire un `commission_due_cents` venu du backend : le % ne
 * vit pas dans Neon, il vit dans Convex. Un backend qui le calcule doit
 * d'abord aller le chercher par HTTP — c'est exactement la plomberie que
 * cette décision retire.
 */

/** Frais retenus par la plateforme de paiement / fanship avant que le brut
 *  n'arrive à la créatrice. La commission du chatteur s'applique au NET.
 *  🪤 DOIT rester aligné avec `PLATFORM_FEE_PERCENT` côté backend
 *  (`app/domain/services/sales_currency.py`).
 *
 *  🩸 15 % de mai au 17-09-2026 — Kayla, en lisant « Ma part » : « les frais
 *  de Dropfans, c'est 10 %. Je m'étais trompée depuis le début. » Rien n'est
 *  rétro-calculé : le taux est vivant, tout ce qui n'est pas encore versé se
 *  recalcule tout seul, ce qui est marqué payé reste verrouillé. */
export const PLATFORM_FEE_PERCENT = 10;

/** Brut → net : 0.9. */
export const NET_REVENUE_MULTIPLIER = (100 - PLATFORM_FEE_PERCENT) / 100;

/**
 * Brut → net RishFans : 0,76. L'assiette `net_rishfans` d'une règle d'associé
 * (Kayla, 14-10-2026) : 10 % de paiement · 10 % de vente sur le reste · 5 % de
 * plateforme ⇒ 76 % du brut. Remplace `NET_REVENUE_MULTIPLIER` ET la déduction des
 * chatteurs (`netOfChatters` est alors ignoré).
 *
 * 🪤 CROSS-REF `Francis app/domain/services/partenaires/grille.py::NET_RISHFANS_POURCENT`
 * (76). Les deux DOIVENT rester alignés : le Portefeuille affiche ce que le
 * backend verse.
 */
export const NET_RISHFANS = 0.76;

/**
 * Commission due (cents USD, arrondie) pour un brut et un % donnés.
 *
 * Retourne `null` quand le % est inconnu (membre sans commission configurée,
 * ou membership pas encore résolue côté Convex). L'appelant affiche alors
 * « — », **jamais « $0 »** : une absence d'information n'est pas un zéro, et
 * c'est précisément la confusion qui a laissé ce bug invisible trois mois.
 */
export function commissionFromGrossCents(
  grossUsdCents: number,
  percent: number | null | undefined,
): number | null {
  if (percent == null || !Number.isFinite(percent)) return null;
  if (!Number.isFinite(grossUsdCents)) return null;
  return Math.round((grossUsdCents * NET_REVENUE_MULTIPLIER * percent) / 100);
}
